import apiClient from "./client";

const API_BASE = process.env.REACT_APP_API_URL || "/api/v1";

export async function getTickets({ all = false } = {}) {
  const params = all ? "?all=true" : "";
  return apiClient.get(`/tickets${params}`);
}

export async function getTicket(id) {
  return apiClient.get(`/tickets/${id}`);
}

export async function createTicket({
  subject,
  message,
  ticketType,
  supportServer,
  minecraftUsername,
  customFields,
  attachments = [],
}) {
  if (attachments.length === 0) {
    return apiClient.post("/tickets", {
      subject,
      message,
      ticketType,
      supportServer,
      ...(minecraftUsername ? { minecraftUsername } : {}),
      ...(customFields ? { customFields } : {}),
    });
  }

  const formData = new FormData();
  formData.append("subject", subject);
  formData.append("message", message);
  if (ticketType) {
    formData.append("ticketType", ticketType);
  }
  if (supportServer) {
    formData.append("supportServer", supportServer);
  }
  if (minecraftUsername) {
    formData.append("minecraftUsername", minecraftUsername);
  }
  if (customFields) {
    formData.append("customFields", JSON.stringify(customFields));
  }
  attachments.forEach((file) => formData.append("attachments", file));

  const headers = {};
  if (apiClient.accessToken) {
    headers["Authorization"] = `Bearer ${apiClient.accessToken}`;
  }

  const response = await fetch(`${API_BASE}/tickets`, {
    method: "POST",
    headers,
    body: formData,
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Failed to create ticket");
  }
  return data;
}

export async function replyToTicket(id, content, attachments = [], options = {}) {
  const isInternalNote = Boolean(options.isInternalNote);
  if (attachments.length === 0) {
    return apiClient.post(`/tickets/${id}/messages`, { content, isInternalNote });
  }

  const formData = new FormData();
  formData.append("content", content);
  formData.append("isInternalNote", String(isInternalNote));
  attachments.forEach((file) => formData.append("attachments", file));

  const headers = {};
  if (apiClient.accessToken) {
    headers["Authorization"] = `Bearer ${apiClient.accessToken}`;
  }

  const response = await fetch(`${API_BASE}/tickets/${id}/messages`, {
    method: "POST",
    headers,
    body: formData,
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Failed to reply");
  }
  return data;
}

export async function updateTicketStatus(id, status) {
  return apiClient.patch(`/tickets/${id}/status`, { status });
}

export async function updateTicketType(id, ticketType, customFields) {
  return apiClient.patch(`/tickets/${id}/type`, {
    ticketType,
    ...(customFields !== undefined ? { customFields } : {}),
  });
}

export async function updateTicketCustomFields(id, customFields) {
  return apiClient.patch(`/tickets/${id}/custom-fields`, { customFields });
}

export async function updateTicketAwaitingReply(id, awaitingReplyFrom) {
  return apiClient.patch(`/tickets/${id}/awaiting-reply`, { awaitingReplyFrom });
}

export async function assignTicketStaff(id, assignedStaffId) {
  return apiClient.patch(`/tickets/${id}/assignee`, { assignedStaffId: assignedStaffId || null });
}

export async function getTicketAssignableStaff() {
  return apiClient.get("/tickets/assignable-staff");
}

export async function getTicketMentionSuggestions(id, query) {
  const qs = new URLSearchParams({ q: query });
  return apiClient.get(`/tickets/${id}/mention-suggestions?${qs.toString()}`);
}

export async function editTicketMessage(ticketId, messageId, content) {
  return apiClient.patch(`/tickets/${ticketId}/messages/${messageId}`, { content });
}

export async function deleteTicketMessage(ticketId, messageId) {
  return apiClient.delete(`/tickets/${ticketId}/messages/${messageId}`);
}

export async function getTicketMessageRevisions(ticketId, messageId) {
  return apiClient.get(`/tickets/${ticketId}/messages/${messageId}/revisions`);
}
